import { useState, useEffect } from 'react';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import Select from '@/components/common/Select';

/**
 * Form for creating or editing an AI instruction
 */
export default function AiInstructionForm({ instruction = null, onSubmit, onCancel, isLoading }) {
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    instruction_text: '',
    scope: 'custom',
    priority: 50,
    enabled: true
  });

  const [errors, setErrors] = useState({});

  // Populate form when editing
  useEffect(() => {
    if (instruction) {
      setFormData({
        name: instruction.name || '',
        display_name: instruction.display_name || '',
        instruction_text: instruction.instruction_text || '',
        scope: instruction.scope || 'custom',
        priority: instruction.priority !== undefined ? instruction.priority : 50,
        enabled: instruction.enabled !== undefined ? instruction.enabled : true
      });
    }
  }, [instruction]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!instruction) { // Only validate name when creating
      if (!formData.name.trim()) {
        newErrors.name = 'Name is required';
      } else if (!/^[a-z][a-z0-9_]*$/.test(formData.name)) {
        newErrors.name = 'Name must be lowercase with underscores (e.g., focus_family_emails)';
      }
    }

    if (!formData.display_name.trim()) {
      newErrors.display_name = 'Display name is required';
    }

    if (!formData.instruction_text.trim()) {
      newErrors.instruction_text = 'Instruction text is required';
    }

    if (formData.priority < 0 || formData.priority > 100) {
      newErrors.priority = 'Priority must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name - only show when creating */}
      {!instruction && (
        <div>
          <label htmlFor="name" className="block text-sm font-medium mb-1">
            Name (Unique Identifier)
          </label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="focus_family_emails"
            disabled={isLoading}
          />
          {errors.name && (
            <p className="text-sm text-red-500 mt-1">{errors.name}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            Lowercase with underscores. Cannot be changed later.
          </p>
        </div>
      )}

      {/* Display Name */}
      <div>
        <label htmlFor="display_name" className="block text-sm font-medium mb-1">
          Display Name
        </label>
        <Input
          id="display_name"
          value={formData.display_name}
          onChange={(e) => handleChange('display_name', e.target.value)}
          placeholder="Focus on Family Emails"
          disabled={isLoading}
        />
        {errors.display_name && (
          <p className="text-sm text-red-500 mt-1">{errors.display_name}</p>
        )}
      </div>

      {/* Instruction Text */}
      <div>
        <label htmlFor="instruction_text" className="block text-sm font-medium mb-1">
          Instruction Text
        </label>
        <textarea
          id="instruction_text"
          value={formData.instruction_text}
          onChange={(e) => handleChange('instruction_text', e.target.value)}
          placeholder="Focus on emails from my family. Always mention those first in reports."
          disabled={isLoading}
          rows={4}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {errors.instruction_text && (
          <p className="text-sm text-red-500 mt-1">{errors.instruction_text}</p>
        )}
        <p className="text-xs text-muted-foreground mt-1">
          This text will be shown to the AI when processing emails.
        </p>
      </div>

      {/* Scope */}
      <div>
        <label htmlFor="scope" className="block text-sm font-medium mb-1">
          Scope
        </label>
        <Select
          id="scope"
          value={formData.scope}
          onChange={(e) => handleChange('scope', e.target.value)}
          disabled={isLoading || (instruction && instruction.is_system)}
        >
          <option value="global">Global (all AI operations)</option>
          <option value="secretary">Secretary (digest generation)</option>
          <option value="analysis">Analysis (email categorization)</option>
          <option value="create_event">Events (event creation)</option>
          <option value="pending_reply">Replies (reply handling)</option>
          <option value="custom">Custom</option>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          Where this instruction applies.
        </p>
      </div>

      {/* Priority */}
      <div>
        <label htmlFor="priority" className="block text-sm font-medium mb-1">
          Priority (0-100)
        </label>
        <Input
          id="priority"
          type="number"
          min="0"
          max="100"
          value={formData.priority}
          onChange={(e) => handleChange('priority', parseInt(e.target.value, 10))}
          disabled={isLoading}
        />
        {errors.priority && (
          <p className="text-sm text-red-500 mt-1">{errors.priority}</p>
        )}
        <p className="text-xs text-muted-foreground mt-1">
          Higher priority instructions are applied first. System instructions are at 100.
        </p>
      </div>

      {/* Enabled Toggle */}
      <div className="flex items-center gap-2">
        <input
          id="enabled"
          type="checkbox"
          checked={formData.enabled}
          onChange={(e) => handleChange('enabled', e.target.checked)}
          disabled={isLoading}
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
        />
        <label htmlFor="enabled" className="text-sm font-medium">
          Enabled
        </label>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          loading={isLoading}
        >
          {instruction ? 'Update' : 'Create'} Instruction
        </Button>
      </div>
    </form>
  );
}
